// In-memory state behind mockApi (src/lib/mock-data/index.ts), one state per patient.
//
// The patient is chosen by the signed-in email: the 21 gold-set patients from
// oneself-platform/data/seed/patients.csv have accounts <member-id>@oneselfpath.test
// in oneselfpath-dev (see tmp/INT-38/test-accounts.md). Any other email (and mock auth)
// gets the synthetic demo patient Alex Rivera.
//
// Paths, waypoints and prompts are the real plain-language guideline text from the EBG
// knowledge graph export (consumer_title / consumer_description / consumer_prompt);
// guideline_text is never used. The patient's zone (GREEN / YELLOW / RED / GRAY) shapes
// progress and priority so each account shows "their version" of the content. The real
// version of this is oneselfpath-service reading pipeline runs (INT-39).
import type {
  AssignedPath,
  Condition,
  Encounter,
  MedicationAdministration,
  MedicationRequest,
  Notification,
  Observation,
  PathSession,
  Practitioner,
  Procedure,
  Prompt,
  QuestionSuggestion,
  ServiceRequest,
  User,
  UserPreferences,
} from "@/api/types";
import guidelines from "./guidelines.json";
import patients from "./patients.json";

type GuidelineFixture = (typeof guidelines)[number];
type PatientFixture = (typeof patients)[number];
type Zone = "GREEN" | "YELLOW" | "RED" | "GRAY";

const now = new Date();
const daysAgo = (n: number) => new Date(now.getTime() - n * 86_400_000).toISOString();
const dateOnly = (iso: string) => iso.slice(0, 10);

const base = (id: number, created = daysAgo(30)) => ({
  id,
  uuid: `00000000-0000-4000-8000-${String(id).padStart(12, "0")}`,
  created_at: created,
  updated_at: created,
  created_by: null,
  updated_by: null,
});

export type RecentSearch = {
  id: number;
  search_keyword: string;
  selected_question: QuestionSuggestion | null;
  created_at: string;
};

export type PatientState = {
  USER_ID: number;
  user: User;
  preferences: UserPreferences;
  paths: AssignedPath[];
  sessions: PathSession[];
  notifications: Notification[];
  questionSuggestions: QuestionSuggestion[];
  recentSearches: RecentSearch[];
  conditions: Condition[];
  encounters: Encounter[];
  observations: Observation[];
  medicationRequests: MedicationRequest[];
  medicationAdministrations: MedicationAdministration[];
  practitioners: Practitioner[];
  procedures: Procedure[];
  serviceRequests: ServiceRequest[];
  refreshPath: (path: AssignedPath) => AssignedPath;
};

// ---------------------------------------------------------------------------
// Patient profiles
// ---------------------------------------------------------------------------
const DEMO: PatientFixture = {
  member_id: "DEMO",
  email: "alex.rivera@example.com",
  first_name: "Alex",
  last_name: "Rivera",
  gender: "male",
  date_of_birth: "1968-03-14",
  condition: "HTN",
  zone: "YELLOW",
  fhir_id: "85b1b457-fd33-51cd-950c-8fe2fb9d5620",
  conditions: [
    { code: "59621000", name: "Essential hypertension" },
    { code: "44054006", name: "Type 2 diabetes mellitus" },
    { code: "55822004", name: "Hyperlipidaemia" },
  ],
  guidelines: ["NG136", "NG28", "NG238"],
};

function patientFor(email?: string | null): PatientFixture {
  const e = (email ?? "").trim().toLowerCase();
  return patients.find((p) => p.email === e) ?? DEMO;
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------
type PathStatus = AssignedPath["status"];

/** How the zone shows up in the first (primary-condition) path. Other paths start "new". */
function primaryPlan(zone: Zone): { status: PathStatus; completed: number; startedDaysAgo: number | null } {
  switch (zone) {
    case "GREEN":
      return { status: "completed", completed: 99, startedDaysAgo: 40 };
    case "YELLOW":
      return { status: "in_progress", completed: 2, startedDaysAgo: 12 };
    case "RED":
      return { status: "in_progress", completed: 0, startedDaysAgo: 3 };
    default:
      return { status: "new", completed: 0, startedDaysAgo: null };
  }
}

function buildPath(
  id: number,
  nextSessionId: { n: number },
  g: GuidelineFixture,
  status: PathStatus,
  completedSessions: number,
  startedDaysAgo: number | null,
  zone: Zone,
  userId: number,
): { path: AssignedPath; sessions: PathSession[] } {
  const sessions: PathSession[] = g.primary_sections.map((ps, i) => {
    const prompts: Prompt[] = ps.prompts.map((p) => ({
      prompt_id: p.ost_rec_code ?? `${g.guideline_number}-${p.reference_number}`,
      prompt_text: p.consumer_prompt,
      is_bookmarked: false,
      rating: 0,
    }));
    let sessionStatus: PathSession["status"] = "new";
    if (status === "completed" || i < completedSessions) sessionStatus = "completed";
    else if (status === "in_progress" && i === completedSessions) sessionStatus = "current";
    else if (status === "in_progress") sessionStatus = "in_progress";
    const priority: PathSession["priority"] = zone === "RED" && i < 2 ? "high" : i === 0 ? "high" : i === 1 ? "medium" : "low";
    return {
      ...base(nextSessionId.n++),
      path: id,
      rank: i + 1,
      title: ps.consumer_title,
      reference_number: ps.reference_number ?? "",
      description: ps.consumer_description ?? "",
      duration: 10,
      time_spent: sessionStatus === "completed" ? "00:08:30" : "00:00:00",
      status: sessionStatus,
      is_bookmarked: i === 1 && status !== "new",
      rating: sessionStatus === "completed" ? (zone === "GREEN" ? 5 : 4) : 0,
      priority,
      recommendations: prompts,
    };
  });

  const path: AssignedPath = {
    ...base(id, daysAgo(30)),
    user: userId,
    name: g.condition ?? g.title ?? g.guideline_number,
    description: g.description ?? g.title ?? "",
    level: "beginner",
    status,
    start_date: startedDaysAgo === null ? null : dateOnly(daysAgo(startedDaysAgo)),
    end_date: status === "completed" ? dateOnly(daysAgo(3)) : null,
    tags: ["Lifestyle", "Prevention", "Nutrition"],
    path_sessions: sessions,
    avg_duration: 10,
    progress: 0,
  };
  return { path, sessions };
}

function healthRows(p: PatientFixture, userId: number) {
  const rec = (id: number, created: string) => ({ ...base(id, created), user: userId, external_id: `${p.member_id}-${id}` });
  const has = (code: string) => p.conditions.some((c) => c.code === code);

  const conditions: Condition[] = p.conditions.map((c, i) => ({
    ...rec(1 + i, daysAgo(400 - i * 40)),
    condition_name: c.name,
    condition_code: c.code,
  }));

  const observations: Observation[] = [];
  const medicationRequests: MedicationRequest[] = [];
  const serviceRequests: ServiceRequest[] = [];
  const zoneHigh = p.zone === "RED" || p.zone === "GRAY";

  if (has("59621000")) {
    observations.push(
      { ...rec(20, daysAgo(14)), code: "85354-9", display: "Blood pressure panel", effective_datetime: daysAgo(14), category: "vital-signs", is_panel: true, value: zoneHigh ? "162/98" : p.zone === "YELLOW" ? "148/92" : "128/78", unit: "mmHg", components: [] },
      { ...rec(21, daysAgo(44)), code: "85354-9", display: "Blood pressure panel", effective_datetime: daysAgo(44), category: "vital-signs", is_panel: true, value: zoneHigh ? "158/96" : "140/88", unit: "mmHg", components: [] },
    );
    medicationRequests.push({ ...rec(30, daysAgo(14)), status: "active", medication_code: "329528", medication_display: "Amlodipine 5 mg tablet", medication_system: "RxNorm", medication_text: "Amlodipine 5 mg once daily" });
  }
  if (has("44054006")) {
    observations.push({ ...rec(22, daysAgo(95)), code: "4548-4", display: "Hemoglobin A1c", effective_datetime: daysAgo(95), category: "laboratory", is_panel: false, value: zoneHigh ? "9.1" : p.zone === "YELLOW" ? "7.4" : "6.6", unit: "%", components: [] });
    medicationRequests.push({ ...rec(31, daysAgo(95)), status: "active", medication_code: "861007", medication_display: "Metformin 500 mg tablet", medication_system: "RxNorm", medication_text: "Metformin 500 mg twice daily" });
    serviceRequests.push({ ...rec(60, daysAgo(14)), code_code: "4548-4", code_display: "HbA1c in 3 months", status: "active", intent: "order" });
  }
  if (has("88805009")) {
    observations.push(
      { ...rec(23, daysAgo(7)), code: "29463-7", display: "Body weight", effective_datetime: daysAgo(7), category: "vital-signs", is_panel: false, value: zoneHigh ? "96" : "91", unit: "kg", components: [] },
      { ...rec(24, daysAgo(30)), code: "30934-4", display: "BNP", effective_datetime: daysAgo(30), category: "laboratory", is_panel: false, value: zoneHigh ? "620" : p.zone === "YELLOW" ? "310" : "120", unit: "pg/mL", components: [] },
    );
    medicationRequests.push({ ...rec(32, daysAgo(30)), status: "active", medication_code: "1719286", medication_display: "Furosemide 40 mg tablet", medication_system: "RxNorm", medication_text: "Furosemide 40 mg each morning" });
  }
  if (has("195967001")) {
    observations.push({ ...rec(25, daysAgo(10)), code: "33452-4", display: "Peak expiratory flow", effective_datetime: daysAgo(10), category: "vital-signs", is_panel: false, value: zoneHigh ? "280" : p.zone === "YELLOW" ? "340" : "420", unit: "L/min", components: [] });
    medicationRequests.push({ ...rec(33, daysAgo(60)), status: "active", medication_code: "745679", medication_display: "Salbutamol 100 mcg inhaler", medication_system: "RxNorm", medication_text: "Salbutamol inhaler as needed" });
  }
  if (has("82423001")) {
    observations.push({ ...rec(26, daysAgo(12)), code: "72514-3", display: "Pain severity (0-10)", effective_datetime: daysAgo(12), category: "survey", is_panel: false, value: zoneHigh ? "8" : p.zone === "YELLOW" ? "5" : "2", unit: "/10", components: [] });
  }
  if (has("55822004")) {
    observations.push({ ...rec(27, daysAgo(95)), code: "13457-7", display: "LDL cholesterol", effective_datetime: daysAgo(95), category: "laboratory", is_panel: false, value: "3.6", unit: "mmol/L", components: [] });
    medicationRequests.push({ ...rec(34, daysAgo(95)), status: "active", medication_code: "617310", medication_display: "Atorvastatin 20 mg tablet", medication_system: "RxNorm", medication_text: "Atorvastatin 20 mg at night" });
  }

  const encounters: Encounter[] = [
    { ...rec(10, daysAgo(14)), status: "finished", class_code: "AMB", class_display: "Ambulatory", start_datetime: daysAgo(14), end_datetime: daysAgo(14), reason_code_text: `${p.conditions[0]?.name ?? "Follow-up"} follow-up`, reason_code_code: p.conditions[0]?.code, reason_code_display: p.conditions[0]?.name },
    { ...rec(11, daysAgo(95)), status: "finished", class_code: "AMB", class_display: "Ambulatory", start_datetime: daysAgo(95), end_datetime: daysAgo(95), reason_code_text: "Annual review", reason_code_code: p.conditions[0]?.code, reason_code_display: p.conditions[0]?.name },
  ];
  const practitioners: Practitioner[] = [{ ...rec(40, daysAgo(400)), first_name: "Priya", last_name: "Natarajan", active: true }];
  const procedures: Procedure[] = [{ ...rec(50, daysAgo(200)), code_code: "252275004", code_display: "Haematology test", performed_datetime: daysAgo(200), status: "completed" }];

  return { conditions, encounters, observations, medicationRequests, medicationAdministrations: [] as MedicationAdministration[], practitioners, procedures, serviceRequests };
}

const QUESTIONS: Omit<QuestionSuggestion, keyof ReturnType<typeof base>>[] = [
  { keywords: "blood pressure, hypertension, bp, high blood pressure", question: "What do my blood pressure numbers mean?", level: "beginner", explanation: "The top number (systolic) is the pressure when your heart beats. The bottom number (diastolic) is the pressure between beats. Your care team will tell you the range that is right for you.", is_active: true, priority: 10 },
  { keywords: "blood pressure, home monitor, measure, cuff", question: "How do I check my blood pressure at home?", level: "beginner", explanation: "Sit quietly for five minutes, keep your arm supported at heart level, and take two readings a minute apart. Write both down or let the app record them.", is_active: true, priority: 9 },
  { keywords: "diabetes, blood sugar, hba1c, glucose", question: "What is HbA1c and why does it matter?", level: "beginner", explanation: "HbA1c shows your average blood sugar over the last two to three months. It helps you and your care team see whether your diabetes plan is working.", is_active: true, priority: 8 },
  { keywords: "diabetes, diet, food, eating, sugar", question: "What should I eat if I have type 2 diabetes?", level: "beginner", explanation: "Aim for regular meals with vegetables, whole grains, lean protein and fewer sugary drinks and snacks. Small, steady changes work better than strict diets.", is_active: true, priority: 7 },
  { keywords: "heart failure, swelling, weight, fluid, breathless", question: "Why should I weigh myself every day with heart failure?", level: "beginner", explanation: "A quick gain of a kilo or two over a few days usually means fluid is building up. Catching it early lets your care team adjust your water tablets before you feel breathless.", is_active: true, priority: 7 },
  { keywords: "asthma, inhaler, reliever, preventer, wheeze", question: "What is the difference between my reliever and preventer inhalers?", level: "beginner", explanation: "The reliever opens your airways quickly when you have symptoms. The preventer is taken every day to keep the airways calm so you need the reliever less.", is_active: true, priority: 7 },
  { keywords: "pain, chronic pain, exercise, flare, back", question: "Is it safe to exercise with chronic pain?", level: "beginner", explanation: "Gentle, regular movement usually helps more than rest. Start small, build up slowly, and expect some ups and downs; your care team can help you plan it.", is_active: true, priority: 6 },
  { keywords: "cholesterol, statin, lipids, heart risk", question: "Why have I been offered a statin?", level: "intermediate", explanation: "Statins lower cholesterol and reduce the chance of a heart attack or stroke. They are offered when your overall risk over the next ten years is raised.", is_active: true, priority: 6 },
  { keywords: "exercise, activity, walking, fitness", question: "How much exercise should I be doing?", level: "beginner", explanation: "Most adults should aim for about 150 minutes of moderate activity a week, such as brisk walking, plus two sessions that strengthen muscles.", is_active: true, priority: 5 },
];

function buildState(p: PatientFixture): PatientState {
  const USER_ID = 1;
  const zone = p.zone as Zone;
  const user: User = {
    ...base(USER_ID, daysAgo(45)),
    password: "",
    email: p.email,
    first_name: p.first_name,
    middle_name: null,
    last_name: p.last_name,
    phone_number: "+1 555 010 2030",
    date_of_birth: p.date_of_birth,
    gender: (p.gender === "male" || p.gender === "female" ? p.gender : "other") as User["gender"],
    address: { street_address: "12 Harbor Lane", city: "Providence", state: "RI", zip_code: "02903", country: "USA" },
    profile_picture: null,
    is_patient: true,
    is_staff: false,
    is_superuser: false,
    is_active: true,
    skip_intro: false,
    has_seen_welcome_tour: false,
    deactivated_at: null,
    is_onboarded: true,
    external_id: p.fhir_id,
    terms_and_conditions_accepted: true,
    date_joined: daysAgo(45),
    last_login: daysAgo(0),
    role: null,
    groups: [],
    user_permissions: [],
  };

  const preferences: UserPreferences = {
    ...base(1),
    user: USER_ID,
    language: "English",
    dark_theme: false,
    notifications: { notifications: true, sound: true, floating_notification: true, lock_screen_notification: false },
  };

  // Paths: one per guideline the patient's conditions map to; the first carries the zone.
  const sessionIds = { n: 100 };
  const plan = primaryPlan(zone);
  const built = p.guidelines
    .map((n) => guidelines.find((g) => g.guideline_number === n))
    .filter((g): g is GuidelineFixture => !!g)
    .map((g, i) =>
      i === 0
        ? buildPath(
            i + 1,
            sessionIds,
            g,
            plan.status,
            // an in-progress path always keeps at least one waypoint open
            plan.status === "in_progress" ? Math.min(plan.completed, g.primary_sections.length - 1) : plan.completed,
            plan.startedDaysAgo,
            zone,
            USER_ID,
          )
        : buildPath(i + 1, sessionIds, g, "new", 0, null, zone, USER_ID),
    );
  const paths = built.map((b) => b.path);
  const sessions = built.flatMap((b) => b.sessions);

  const refreshPath = (path: AssignedPath): AssignedPath => {
    const own = sessions.filter((s) => s.path === path.id).sort((a, b) => a.rank - b.rank);
    const done = own.filter((s) => s.status === "completed").length;
    const mutable = path as { -readonly [K in keyof AssignedPath]: AssignedPath[K] };
    mutable.path_sessions = own;
    mutable.progress = own.length ? Math.round((done / own.length) * 100) : 0;
    mutable.updated_at = new Date().toISOString();
    return path;
  };
  paths.forEach(refreshPath);

  const primary = paths[0];
  const notifications: Notification[] = [];
  const note = (id: number, days: number, title: string, message: string, read = false) =>
    notifications.push({ ...base(id, daysAgo(days)), user: USER_ID, title, message, is_read: read, is_sent: true });
  if (paths[1]) note(1, 0, "New Health Path Assigned", `A new path, ${paths[1].name}, has been added based on your latest health data.`);
  switch (zone) {
    case "GREEN":
      note(2, 3, "Path completed", `You completed the ${primary?.name ?? "your"} path. Nice work.`, true);
      note(3, 9, "Health data checked", "We looked at your latest records. No changes in your care recommendations.", true);
      break;
    case "YELLOW":
      note(2, 2, `Keep going on ${primary?.name ?? "your path"}`, `You have not visited your ${primary?.name ?? ""} path in a few days. Pick up where you left off.`);
      break;
    case "RED":
      note(2, 1, "Your care team may be in touch", "Your recent results need a closer look. Please keep taking your medicines and watch for the signs described in your path.");
      note(3, 3, "New Health Path Assigned", `A new path, ${primary?.name ?? ""}, has been added based on your latest health data.`, true);
      break;
    default:
      note(2, 1, "We could not fully check your records", "Some of your health information is missing or out of date. Your care team will follow up; you can still explore your path.");
  }

  const questionSuggestions: QuestionSuggestion[] = QUESTIONS.map((q, i) => ({ ...base(i + 1), ...q }));
  const relevant = questionSuggestions.filter((q) => (p.conditions[0]?.name ?? "").toLowerCase().split(" ").some((w) => w.length > 4 && q.keywords.includes(w)));
  const recentSearches: RecentSearch[] = relevant.slice(0, 2).map((q, i) => ({ id: i + 1, search_keyword: q.keywords.split(",")[0].trim(), selected_question: q, created_at: daysAgo(1 + i * 3) }));

  return { USER_ID, user, preferences, paths, sessions, notifications, questionSuggestions, recentSearches, ...healthRows(p, USER_ID), refreshPath };
}

// ---------------------------------------------------------------------------
// Per-patient state cache (mutations persist for the session)
// ---------------------------------------------------------------------------
const states = new Map<string, PatientState>();

export function getState(email?: string | null): PatientState {
  const p = patientFor(email);
  let st = states.get(p.email);
  if (!st) {
    st = buildState(p);
    states.set(p.email, st);
  }
  return st;
}

export const testPatients = patients;

export function paginate<T>(rows: T[], params?: { page?: number; page_size?: number }) {
  const page = Number(params?.page ?? 1);
  const size = Number(params?.page_size ?? 10);
  const start = (page - 1) * size;
  return {
    count: rows.length,
    next: start + size < rows.length ? String(page + 1) : null,
    previous: page > 1 ? String(page - 1) : null,
    results: rows.slice(start, start + size),
  };
}
